import React, { useState, useEffect, useContext } from "react";
import { Box, Grow, SxProps, Theme } from "@mui/material";
import { FormsListItemCard } from "./FormsListItemCard";
import { AtticusForm } from "../../../../models/AtticusForm";
import { EstateContext } from "EstateContextWrapper";
import { SplitButtonOption } from "views/shared/SplitButtonGroup";
import AtticusFormViewDialog from "views/shared/dialog/AtticusFormViewDialog";
import { DetailsDialog } from "views/shared/dialog/DetailsDialog";
import { Estate } from "models/Estate";
import { PdfFileChoice } from "views/shared/dialog/useAtticusFormViewer";
import { AuthContext } from "AuthContextWrapper";
import AtticusFormUpgradeToViewDialog from "views/shared/dialog/AtticusFormUpgradeToViewDialog";
import AtticusFormConfirmRemoveDialog from "views/shared/dialog/AtticusFormConfirmRemoveDialog";
import { Loading } from "views/shared/Loading";
import { LARAVEL_APP_HOST } from "../../../../constants";
import formDetailsIcon from "../../../../assets/form-details-icon.svg"
import { downloadAtticusForm } from "utils/download";

interface Props {
  forms?: AtticusForm[];
  estate?: Estate;
  sx?: SxProps<Theme>;
  truncateFormsAt?: number;
  emptyState?: JSX.Element;
  allowCheckedBadge?: boolean;
}

export const FormsList = ({
  forms,
  estate,
  sx,
  truncateFormsAt,
  emptyState,
  allowCheckedBadge = false,
}: Props): JSX.Element => {
  const { estateForms, addEstateForm, fetchEstateForms, estateFormsIsLoading } = useContext(EstateContext);
  const { subscription } = useContext(AuthContext);
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [formToRemove, setFormToRemove] = useState<AtticusForm | undefined>(undefined);
  const [pdfDialogForm, setPdfDialogForm] = useState<AtticusForm>();
  const [formsToDisplay, setFormsToDisplay] = useState<AtticusForm[] | undefined>(forms);
  const togglePdfDialog = () => {
    setIsPdfDialogOpen(!isPdfDialogOpen);
    if (isPdfDialogOpen === true) {
      setPdfDialogForm(undefined);
    }
  };
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [descriptionDialogForm, setDescriptionDialogForm] = useState<AtticusForm>();
  const toggleDescriptionDialog = () => {
    setDescriptionDialogOpen(!descriptionDialogOpen);
    if (descriptionDialogOpen === true) {
      setDescriptionDialogForm(undefined);
    }
  };
  const redirectToAfterUpgrade = encodeURIComponent(
    window.location.protocol +
      "//" +
      window.location.host +
      "/app/forms-and-files"
  );

  useEffect(() => {
    setFormsToDisplay(forms);
  }, [forms]);

  useEffect(() => {
    if (forms && estateForms) {
      const updates = Object.fromEntries(
        estateForms.map((form) => [form.id, form])
      );
      const displayForms = forms.map((f) => updates[f.id] || f);
      setFormsToDisplay(displayForms);
    }
  }, [forms, estateForms]);

  const getFormOptions = (form: AtticusForm): SplitButtonOption[] => {
    const formOptions: SplitButtonOption[] = [];

    if(subscription?.isActive) {
      formOptions.push({
        label: "View",
        key: "view",
        onClick: async () => {
          if (form) {
            setPdfDialogForm(form);
            togglePdfDialog();
          }
        },
      })

      formOptions.push({
        label: "Download",
        key: "download",
        onClick: async () => {
          if (form) {
            downloadAtticusForm(form)
          }
        },
      })
    } else {
      formOptions.push({
        label: "Upgrade",
        key: "upgrade",
        onClick: async () => {
          window.location.href = `${LARAVEL_APP_HOST}/payment-plan?redirectToAfterUpgrade=${redirectToAfterUpgrade}`
        },
      })
    }

    if (estate && estateForms?.find((estateForm) => estateForm.id == form.id)) {
      formOptions.push({
        label: "Remove from My Forms",
        key: "remove",
        hidden: !estateForms || !estateForms?.find((ef) => ef.id == form.id),
        onClick: async () => {
          if (form && estate.id) {
            setFormToRemove(form);
          }
        },
      });
    } else if (estate && estateForms) {
      formOptions.push({
        label: "Add to My Forms",
        key: "add",
        hidden: !estateForms || !!estateForms?.find((ef) => ef.id == form.id),
        onClick: async () => {
          if (form && estate.id) {
            addEstateForm(estate.id, form);
          }
        },
      });
    }

    return formOptions;
  };

  const renderForms = (forms?: AtticusForm[]): JSX.Element[] => {
    const rows = [];
    if (!forms) {
      for (let formI = 0; formI < 3; formI++) {
        rows.push(
          <Grow in key={formI} timeout={formI * 100}>
            <Box>
              <FormsListItemCard
                pdfDialogOpen={isPdfDialogOpen}
                setPdfDialogForm={setPdfDialogForm}
                togglePdfDialog={togglePdfDialog}
                descriptionDialogOpen={descriptionDialogOpen}
                setDescriptionDialogForm={setDescriptionDialogForm}
                toggleDescriptionDialog={toggleDescriptionDialog}
                sx={{
                  padding: "1rem",
                  marginBottom: "1rem",
                }}
              />
            </Box>
          </Grow>
        );
      }
    } else {
      (truncateFormsAt ? forms.slice(0, truncateFormsAt) : forms).forEach(
        (form, index) => {
          rows.push(
            <Grow in key={form.id} timeout={index * 100}>
              <Box>
                <FormsListItemCard
                  form={form}
                  estate={estate}
                  pdfDialogOpen={isPdfDialogOpen}
                  setPdfDialogForm={setPdfDialogForm}
                  togglePdfDialog={togglePdfDialog}
                  descriptionDialogOpen={descriptionDialogOpen}
                  setDescriptionDialogForm={setDescriptionDialogForm}
                  toggleDescriptionDialog={toggleDescriptionDialog}
                  sx={{
                    padding: "1rem",
                    marginBottom: "1rem",
                  }}
                  formOptions={getFormOptions(form)}
                  checkedBadge={
                    allowCheckedBadge && form.estateFormMap ? true : false
                  }
                />
              </Box>
            </Grow>
          );
        }
      );
    }
    return rows;
  };

  const onRemovedForm = () => {
    setFormToRemove(undefined);
    if (estate?.id) {
      fetchEstateForms(estate.id);
    }
  };

  return (
    <Box sx={sx}>
      {subscription?.isActive ? (
        <AtticusFormViewDialog
          open={isPdfDialogOpen}
          onClose={togglePdfDialog}
          form={pdfDialogForm}
          fileChoice={PdfFileChoice.PROCESSED}
          withDisclaimer
        />
      ) : (
        <AtticusFormUpgradeToViewDialog
          open={isPdfDialogOpen}
          onClose={togglePdfDialog}
          form={pdfDialogForm}
        />
      )}
      {formToRemove && estate?.id ? (
        <AtticusFormConfirmRemoveDialog
          open={!!formToRemove}
          onClose={onRemovedForm}
          form={formToRemove}
          estateId={estate?.id}
        />
      ) : null}
      <DetailsDialog
        open={descriptionDialogOpen}
        headerImageSrc={formDetailsIcon}
        headerImageAlt="A magnifying glass observing a form's text"
        title={descriptionDialogForm?.formTitle || "Form Details"}
        subtitle={`${descriptionDialogForm?.geography?.fullName
          ? descriptionDialogForm?.geography?.fullName
          : ""
          }${descriptionDialogForm?.geography?.fullName &&
            descriptionDialogForm?.formGovernmentId
            ? " | "
            : ""
          }${descriptionDialogForm?.formGovernmentId
            ? descriptionDialogForm?.formGovernmentId
            : ""
          }`}
        content={
          descriptionDialogForm?.formDescription || "No Description Available."
        }
        onConfirm={toggleDescriptionDialog}
        confirmText="Ok"
        maxWidth="md"
      />

      <Box>
        {!estateFormsIsLoading &&
          (!formsToDisplay || formsToDisplay.length > 0) ? (
          renderForms(formsToDisplay)
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 4,
              padding: 4,
            }}
          >
            {estateFormsIsLoading ? (
              <Loading />
            ) : formsToDisplay ? (
              <>{emptyState}</>
            ) : null}
          </Box>
        )}
      </Box>
    </Box>
  );
};
